import { Button } from '@/components/ui/button'
import { useStudioLocale } from '@/i18n'

interface DataLoadErrorProps {
  /**
   * What could not be read, in the words the surrounding screen uses.
   *
   * Only used to BUILD the English sentence below. A localized surface
   * passes a whole `message` instead — a sentence assembled from a noun and
   * an English frame is not translatable, whatever language the noun is in.
   */
  label?: string
  onRetry: () => void
  testId: string
  /** Fully authored surrounding-surface message, when grammar needs more than a label. */
  message?: string
  /** Overrides the active locale's own retry word. */
  retryLabel?: string
}

/**
 * What a dashboard section shows when its read failed.
 *
 * This exists because the previous behaviour was to show generated numbers in
 * exactly this situation. The failure detail stays in `console.error`; the user
 * is told the read failed and given the retry, and no value is rendered.
 */
export function DataLoadError({
  label,
  onRetry,
  testId,
  message,
  retryLabel,
}: DataLoadErrorProps) {
  const { strings } = useStudioLocale()
  const retry = retryLabel ?? strings.businessScreens.review.tryAgain
  return (
    <div
      className="rounded-xl border border-border bg-card p-6 text-center"
      data-testid={testId}
      role="alert"
    >
      <p className="text-sm font-medium text-foreground">
        {message ?? `We couldn’t load ${label} right now.`}
      </p>
      <Button variant="outline" size="sm" className="mt-3 min-h-10" onClick={onRetry}>
        {retry}
      </Button>
    </div>
  )
}
